import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import {
  DocumentOfReceive,
  ReceiveStatus,
} from "../../models/DocumentOfReceive";
import { Product, ProductType } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { addNewDocument } from "../../services/NewVersionService";
import "./InitialStateFromExcelPage.scss";

type InventoryType = "mercantile" | "repromaterial";

type ExcelRow = {
  SifraRobe: string;
  Naziv: string;
  Oznaka: string;
  Kolicina: number;
  Srps?: number;
};

type SkippedRow = {
  code: string;
  name: string;
  qty: string;
  reason: "UNKNOWN_CODE" | "INVALID_ROW";
};

export const InitialStateFromExcelPage = () => {
  const { t } = useTranslation();
  const { state } = useApplicationContext();
  const toast = useRef<any>(null);

  // poslovna godina, editabilna (default global state)
  const [businessYear, setBusinessYear] = useState<number>(
    state.businessYear ?? new Date().getFullYear(),
  );

  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [selectedType, setSelectedType] = useState<InventoryType>("mercantile");
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [parsedRows, setParsedRows] = useState<ExcelRow[]>([]);
  const [skippedRows, setSkippedRows] = useState<SkippedRow[]>([]);
  const [showInstructions, setShowInstructions] = useState(false);
  const [processing, setProcessing] = useState(false);

  const dateForInitialState = useMemo(() => {
    // početno stanje je 01.01 izabrane poslovne godine u 08:00
    return new Date(businessYear, 0, 1, 0, 8, 0, 0);
  }, [businessYear]);

  const businessYearOptions = useMemo(() => {
    const start = 2020;
    const end = new Date().getFullYear() + 1;
    const arr: { id: number; name: string }[] = [];
    for (let y = start; y <= end; y++) {
      arr.push({ id: y, name: y.toString() });
    }
    return arr;
  }, []);

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({ mode: "all" });

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        const [wResp, pResp] = await Promise.all([
          getAllWarehouses(),
          getAllProducts(),
        ]);
        [wResp, pResp].forEach(ensureSuccess);

        const wh: Warehouse[] = wResp?.data?.result ?? [];
        const pr: Product[] = pResp?.data?.result ?? [];

        setWarehousesApi(wh);
        setProductsApi(pr);
      } catch (e: any) {
        console.error(e);
        toast.current?.show({
          severity: "error",
          detail:
            e?.response?.data?.message ?? t("Greška pri učitavanju šifarnika"),
          life: 4000,
        });
        setWarehousesApi([]);
        setProductsApi([]);
      } finally {
        setProcessing(false);
      }
    };

    run();
  }, []);

  const onSelectedWarehouseChange = (_prop: string, value: string) => {
    setSelectedWarehouse(value);
  };

  const onSelectedTypeChange = (_prop: string, value: InventoryType) => {
    setSelectedType(value);
    setParsedRows([]);
    setSkippedRows([]);
    setSelectedFileName("");
  };

  const onBusinessYearDropdownChange = (_prop: string, value: any) => {
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed)) setBusinessYear(parsed);
  };

  const showToast = (
    severity: "success" | "info" | "warn" | "error",
    detail: string,
    life = 4000,
  ) => {
    toast.current?.show({ severity, detail, life });
  };

  const validateExcelHeaders = (row: any, type: InventoryType) => {
    const baseOk =
      row &&
      "SifraRobe" in row &&
      "Naziv" in row &&
      "Oznaka" in row &&
      "Kolicina" in row;

    if (!baseOk) return false;

    if (type === "mercantile") {
      return "Srps" in row;
    }
    return true;
  };

  const parseExcel = async (file: File, type: InventoryType) => {
    setProcessing(true);
    setParsedRows([]);
    setSkippedRows([]);

    try {
      const xlsx = await import("xlsx");
      const buf = await file.arrayBuffer();
      const workbook = xlsx.read(buf, { type: "array" });

      const firstSheetName = workbook.SheetNames?.[0];
      if (!firstSheetName) {
        showToast("error", t("Excel fajl nema sheet."));
        return;
      }

      const worksheet = workbook.Sheets[firstSheetName];
      const json = xlsx.utils.sheet_to_json(worksheet, { defval: "" });

      if (!json || json.length === 0) {
        showToast("error", t("Excel fajl je prazan."));
        return;
      }

      // Provera kolona na osnovu prvog reda
      if (!validateExcelHeaders(json[0], type)) {
        showToast(
          "error",
          type === "mercantile"
            ? t(
                "Neispravan format. Očekivane kolone su: SifraRobe, Naziv, Oznaka, Kolicina, Srps.",
              )
            : t(
                "Neispravan format. Očekivane kolone su: SifraRobe, Naziv, Oznaka, Kolicina.",
              ),
          6000,
        );
        return;
      }

      const good: ExcelRow[] = [];
      const skipped: SkippedRow[] = [];

      json.forEach((r: any) => {
        const code = (r.SifraRobe ?? "").toString().trim();
        const name = (r.Naziv ?? "").toString().trim();
        const unit = (r.Oznaka ?? "").toString().trim();

        const rawQty = r.Kolicina;
        const qty =
          typeof rawQty === "number"
            ? rawQty
            : parseFloat((rawQty ?? "").toString().trim());
        const rawSrps = r.Srps;
        const srps =
          typeof rawSrps === "number"
            ? rawSrps
            : parseFloat((rawSrps ?? "").toString().trim());

        // preskoči prazne / neispravne redove
        if (!code || !name || isNaN(qty) || qty <= 0) {
          if (code || name) {
            skipped.push({
              code: code || "-",
              name: name || "-",
              qty: isNaN(qty) ? "-" : qty.toString(),
              reason: "INVALID_ROW",
            });
          }
          return;
        }

        const productMeta = productsApi.find(
          (p) => (p.code ?? "").toString() === code,
        );

        if (!productMeta) {
          skipped.push({
            code,
            name,
            qty: qty.toString(),
            reason: "UNKNOWN_CODE",
          });
          return;
        }

        good.push({
          SifraRobe: code,
          Naziv: name,
          Oznaka: unit,
          Kolicina: qty,
          ...(type === "mercantile" ? { Srps: srps } : {}),
        });
      });

      setParsedRows(good);
      setSkippedRows(skipped);

      const unknownCount = skipped.filter(
        (s) => s.reason === "UNKNOWN_CODE",
      ).length;
      if (unknownCount > 0) {
        showToast(
          "warn",
          `${t(
            "Preskočeno stavki (nepostojeća šifra u šifrarniku)",
          )}: ${unknownCount}`,
          6000,
        );
      } else {
        showToast("success", t("Excel fajl je uspešno učitan."));
      }
    } catch (e) {
      showToast("error", t("Greška pri čitanju Excel fajla."));
    } finally {
      setProcessing(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // .xlsx only
    const isXlsx =
      file.name.toLowerCase().endsWith(".xlsx") ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (!isXlsx) {
      showToast("error", t("Dozvoljen je samo .xlsx Excel fajl."));
      e.target.value = "";
      return;
    }

    setSelectedFileName(file.name);
    await parseExcel(file, selectedType);
  };

  const buildBaseDocument = (warehouse: Warehouse): DocumentOfReceive => {
    return {
      id: "",
      status: ReceiveStatus.Finished,
      warehouse,
      warehouseman: "Početno stanje",
      type: undefined,
      date: dateForInitialState,
      time: dateForInitialState,

      brutto: undefined,
      tara: 0,
      netto: undefined,
      srps: 0,

      product: {} as Product,
      productWithCode: "",

      repromaterialProducts: [],
      repromaterialProductsJSON: "",

      partner: {} as Partner,
      shipper: {} as Partner,
      driverName: "Početno stanje",
      vehicleNumber: "",

      moisture: 0,
      impurity: 0,
      breakage: 0,
      defect: 0,
      hectoliter: 0,

      protein: undefined,
      gluten: undefined,
      cellNumberFloor: undefined,
      hybrid: "",
      dryingCosts: undefined,
      dryWeight: undefined,
      shippingSlip: "",
      calculationMethod: false,
      foreignProduct: false,
      productOwner: {} as Partner,
      note: "Početno stanje na osnovu excel popisne liste",
      finishedDocument: true,
    } as DocumentOfReceive;
  };

  const createInitialState = async () => {
    if (!selectedWarehouse || !selectedType) return;
    if (!selectedFileName) {
      showToast("error", t("Morate izabrati Excel fajl."));
      return;
    }

    const warehouse = warehousesApi.find(
      (w) => w.id === selectedWarehouse,
    ) as Warehouse;
    if (!warehouse?.id) {
      showToast("error", t("Morate izabrati magacin."));
      return;
    }

    if (parsedRows.length === 0) {
      showToast(
        "error",
        t("Nema validnih stavki za unos iz Excel fajla."),
        6000,
      );
      return;
    }

    setProcessing(true);
    try {
      if (selectedType === "mercantile") {
        // 1 prijemnica po stavci — sekvencijalno
        for (const row of parsedRows) {
          const productMeta = productsApi.find(
            (p) => (p.code ?? "") === row.SifraRobe,
          );
          if (!productMeta) continue; // trebalo bi da ne upada ovde (već filtrirano)

          const doc = cloneDeep(buildBaseDocument(warehouse));
          doc.product = productMeta as any;
          doc.productWithCode = `${productMeta.code ?? ""} - ${
            productMeta.name ?? ""
          }`;

          doc.netto = row.Kolicina;
          doc.srps = row.Srps as number;
          doc.brutto = row.Kolicina;
          doc.tara = 0;

          // @ts-ignore
          if (!doc.id) delete doc.id;

          doc.mercantile = true as any;
          doc.input = true as any;
          doc.repromaterialProducts = [];
          doc.repromaterialProductsJSON = "[]";

          await addNewDocument(doc as any);
        }

        showToast(
          "success",
          `${t("Uspešno kreirano početno stanje (merkantila). Stavki")}: ${
            parsedRows.length
          }`,
        );
      } else {
        // repromaterijal — jedna prijemnica
        const doc = cloneDeep(buildBaseDocument(warehouse));
        doc.calculationMethod = true;

        const reproProducts: any[] = [];

        parsedRows.forEach((row) => {
          const productMeta = productsApi.find(
            (p) => (p.code ?? "") === row.SifraRobe,
          );
          if (!productMeta) return;

          reproProducts.push({
            id: productMeta.id,
            name: productMeta.name ?? "",
            type: ProductType.RawMaterial,
            code: productMeta.code ?? "",
            category: productMeta.category,
            lot: "",
            amount: row.Kolicina.toString(),
            measuringUnit: productMeta.measuringUnit ?? row.Oznaka ?? "",
          });
        });

        doc.repromaterialProducts = reproProducts as any;
        doc.repromaterialProductsJSON = JSON.stringify(reproProducts);

        // @ts-ignore
        if (!doc.id) delete doc.id;

        doc.mercantile = false as any;
        doc.input = true as any;

        await addNewDocument(doc as any);

        showToast(
          "success",
          `${t("Uspešno kreirano početno stanje (repromaterijal). Stavki")}: ${
            parsedRows.length
          }`,
        );
      }

      // info o preskočenim šiframa
      const unknown = skippedRows.filter((s) => s.reason === "UNKNOWN_CODE");
      if (unknown.length > 0) {
        const codes = unknown.map((x) => x.code).join(", ");
        showToast(
          "info",
          `${t("Preskočeno nepostojećih šifara")}: ${unknown.length}. ${t(
            "Šifre",
          )}: ${codes}`,
          8000,
        );
      }
    } catch (e) {
      showToast(
        "error",
        t("Došlo je do greške prilikom kreiranja početnog stanja."),
        6000,
      );
    } finally {
      setProcessing(false);
    }
  };

  const unknownCodes = skippedRows.filter((s) => s.reason === "UNKNOWN_CODE");

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-upload header-title-icon"
              style={{ fontSize: 16 }}
            ></i>
            <span className="header-title-left">
              {t("Automatski unos početnog stanja iz excel-a")}
            </span>
          </div>
        </div>
        <div className="form-body">
          <DropdownInput
            id="businessYear"
            value={businessYear as any}
            onValueChange={onBusinessYearDropdownChange}
            optionValue="id"
            optionLabel="name"
            options={businessYearOptions}
            filter={false}
            labelText={t("Početno stanje poslovne godine *")}
            placeholderText={t("Izaberite iz liste")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{ required: true }}
            disabled={processing}
          />
          <DropdownInput
            id="selectedWarehouse"
            value={selectedWarehouse}
            onValueChange={onSelectedWarehouseChange}
            optionValue="id"
            optionLabel="name"
            options={warehousesApi.filter((w) => w.virtual !== true)}
            filter={false}
            labelText={t("Magacin *")}
            placeholderText={t("Izaberite iz liste")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{ required: true }}
            disabled={processing}
          />
          <DropdownInput
            id="selectedType"
            value={selectedType}
            onValueChange={onSelectedTypeChange as any}
            optionValue="id"
            optionLabel="name"
            options={[
              { id: "mercantile", name: t("Merkantila") },
              { id: "repromaterial", name: t("Repromaterijal") },
            ]}
            filter={false}
            labelText={t("Tip *")}
            placeholderText={t("Izaberite iz liste")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{ required: true }}
            disabled={processing}
          />
          <div className="file-upload-row">
            <label className="file-label">{t("Excel fajl (.xlsx) *")}</label>
            <div className="file-actions">
              <input
                id="excelFile"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
                disabled={processing}
                className="file-input-hidden"
              />
              <div
                className={`file-picker ${selectedFileName ? "has-file" : ""}`}
                onClick={() => {
                  const el = document.getElementById(
                    "excelFile",
                  ) as HTMLInputElement | null;
                  el?.click();
                }}
              >
                <i
                  className={`bi ${
                    selectedFileName
                      ? "bi-file-earmark-excel"
                      : "bi-folder2-open"
                  }`}
                ></i>
                <span>
                  {selectedFileName ? selectedFileName : t("Izaberi fajl")}
                </span>
              </div>
            </div>
          </div>
          <div className="instructions-toggle">
            <ButtonInput
              labelText={t("Uputstvo")}
              icon="bi bi-info-circle"
              onButtonClick={() => setShowInstructions((prev) => !prev)}
              addStyle={{
                border: "1px solid rgba(0, 165, 83, 1)",
                background: "#ffffff",
                color: "rgba(0, 165, 83, 1)",
              }}
            />
            {showInstructions && (
              <div className="instructions-box">
                <div className="instructions-title">
                  {t("Pravila za upload Excel fajla")}
                </div>
                <ul>
                  <li>{t("Fajl mora biti u .xlsx formatu")}</li>
                  <li>
                    {t(
                      "Mogu se koristiti fajlovi generisani kao popisne liste, eventualno dopunjeni dodatnom robom",
                    )}
                  </li>
                  <li>
                    {t("Obavezne kolone: SifraRobe, Naziv, Oznaka i Kolicina")}
                  </li>
                  {selectedType === "mercantile" && (
                    <li>{t("Za merkantilu je obavezna i kolona: Srps")}</li>
                  )}
                  <li>
                    {t(
                      "Prvi red su obavezni naslovi kolona: SifraRobe, Naziv, Oznaka, Kolicina i Srps za merkantilu. Proizvodi se unose od drugog reda. Broj redova nije ograničen",
                    )}
                  </li>
                  <li>
                    {t("Proizvodi se prepoznaju samo po šifri - SifraRobe")}
                  </li>
                  <li>{t("Količina mora biti broj veći od 0")}</li>
                </ul>
              </div>
            )}
          </div>
          <div className="footer-buttons">
            {processing ? (
              <ProgressSpinner
                style={{ width: "70px", height: "70px", color: "green" }}
                strokeWidth="4"
                animationDuration=".8s"
              />
            ) : (
              <ButtonInput
                labelText={t("Kreiraj početno stanje")}
                icon="bi bi-check2-circle"
                onButtonClick={createInitialState}
                disabled={!selectedWarehouse || !selectedFileName || processing}
                addStyle={{
                  background: "rgba(0, 165, 83, 1)",
                  border: "none",
                  margin: "0 .5rem",
                }}
              />
            )}
          </div>
          {unknownCodes.length > 0 && (
            <div className="skipped-box">
              <div className="skipped-title">
                {t(
                  "Neobrađene stavke (šifra nije pronađena u listi proizvoda/robe)",
                )}
                : <b>{unknownCodes.length}</b>
              </div>
              <ul className="skipped-list">
                {unknownCodes.map((x, idx) => (
                  <li key={`${x.code}-${idx}`}>
                    <b>{x.code}</b> — {x.name} ({x.qty})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};
